// @need-translate
import {HTMLAttributes, useEffect} from 'react';
import {useInView} from 'react-intersection-observer';
import {SchemeLink} from '../../../applink/SchemeLink';
import {twMerge} from '../../../common/twMerge';
import {useIsDarkMode} from '../../../common/useIsDarkMode.hook';
import {useIsResponsiveFoldable} from '../../../common/useIsResponsiveFoldable.hook';
import {Icon} from '../../../design-system/atoms/Icon/Icon';
import {IconType} from '../../../design-system/atoms/Icon/IconType.enum';
import {Image} from '../../../design-system/atoms/Image/Image';
import {Text} from '../../../design-system/atoms/Text/Text';
import {ThumbnailRatio} from '../../../design-system/molecules/ThumbnailWithBadges/ThumbnailRatio.constant';
import {ThumbnailType} from '../../../design-system/molecules/ThumbnailWithBadges/ThumbnailType.enum';
import {aggregateAndSendLog} from '../../../logging/aggregateAndSendLog';
import {LOG_CLICK_EVENT} from '../../../logging/logEvent';

export interface WelcomeBannerItemProps extends HTMLAttributes<HTMLDivElement> {
  bannerTitle: string;
  bannerImgSrc: string;
  bannerAlt?: string;
  scheme: string;
}

export function WelcomeBannerItem({
  bannerTitle,
  bannerImgSrc,
  bannerAlt,
  scheme,
  className,
  ...props
}: WelcomeBannerItemProps) {
  const isDarkMode = useIsDarkMode();
  const isFoldable = useIsResponsiveFoldable();

  const {ref, inView, entry} = useInView();
  useEffect(() => {
    if (inView && entry?.target) {
      aggregateAndSendLog({
        targetElem: entry.target as HTMLElement,
        actionType: 'ViewImp',
        actionName: '버튼_노출',
        customProps: {
          user_action: 'imp',
        },
      });
    }
  }, [entry, inView]);
  return (
    <SchemeLink scheme={scheme || undefined}>
      <div
        className={twMerge(
          'relative h-[104px] w-full cursor-pointer rounded-6pxr bg-theme-solid-10',
          className
        )}
        onClickCapture={e => {
          LOG_CLICK_EVENT('버튼_클릭', e);
        }}
        ref={ref}
        {...props}
      >
        <div
          // eslint-disable-next-line tailwindcss/no-custom-classname
          className={twMerge(
            isFoldable ? 'above-foldable' : 'below-foldable',
            'absolute top-0 left-0 z-10 flex h-full flex-col justify-center rounded-l-6pxr'
          )}
          style={{
            background: isDarkMode
              ? 'linear-gradient(90deg, #222222 -13.44%, rgba(34, 34, 34, 0) 70.63%'
              : 'linear-gradient(90deg, #eeeeee 0%, rgba(238, 238, 238, 0) 100%, rgba(238, 238, 238, 0) 100%',
          }}
        >
          <div>
            <Text className="font-medium1-bold text-theme-solid-100">
              {bannerTitle}
            </Text>
          </div>
          <div className="mt-6pxr flex items-center">
            <Text className="font-small2 text-theme-solid-40">보러가기</Text>{' '}
            <Icon type={IconType.ButtonArrowRight} />
          </div>
        </div>
        <div className="absolute top-0 h-full w-[144px] foldable:right-20pxr under-foldable:right-8pxr">
          <Image
            src={bannerImgSrc}
            ratio={ThumbnailRatio[ThumbnailType.Banner]}
            alt={bannerAlt || '썸네일'}
            className="h-full"
          />
        </div>
      </div>
      <style jsx>
        {`
          .below-foldable {
            width: 184px;
            padding-left: 20px;
            padding-right: 36px;
          }
          .above-foldable {
            width: 300px;
            padding-left: 40px;
            padding-right: 40px;
          }
        `}
      </style>
    </SchemeLink>
  );
}
